import React, { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { domainRequestsAPI } from '@hpanel/web-common/api/client'
import { ErrorBox, cardClass, inputClass, labelClass, toMessage } from '../../components/sites/siteUi'
import { useSlot } from '../../components/sites/useSlot'

/**
 * Ask for a parked domain on one of the tenant's sites
 * (/client/sites/:containerId/park). An administrator approves it. Its own
 * page (CLAUDE.md §15).
 */
export const ParkDomainPage: React.FC = () => {
  const containerId = Number(useParams<{ containerId: string }>().containerId)
  const navigate = useNavigate()
  const { slot, loading, error: loadError } = useSlot(containerId)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const cleanName = name.trim().toLowerCase()
    try {
      await domainRequestsAPI.submit({ kind: 'parked', name: cleanName, container_id: containerId })
      navigate('/client/sites', {
        state: { flash: `Requested ${cleanName} as a parked domain. An administrator will review it.` },
      })
    } catch (err: unknown) {
      setError(toMessage('park domain', err, 'Failed to send the request'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="site-park-page" className="space-y-6 max-w-3xl">
      <div>
        <Link to="/client/sites" className="text-sm text-primary-600 hover:underline">
          ← Sites
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">Park a domain</h1>
        {slot && (
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            The parked domain serves the same site as <strong className="font-mono">{slot.primary.name}</strong>.
            An administrator reviews the request.
          </p>
        )}
      </div>

      <ErrorBox message={error ?? loadError} testId="site-park-error" />

      {loading ? (
        <p className="text-gray-600 dark:text-gray-400">Loading…</p>
      ) : slot ? (
        <form onSubmit={handleSubmit} className={cardClass}>
          <div>
            <label htmlFor="site-park-name" className={labelClass}>Domain to park</label>
            <input
              id="site-park-name"
              data-testid="site-park-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="example.org"
              className={inputClass}
            />
          </div>
          <button
            type="submit"
            data-testid="site-park-submit"
            disabled={saving || !name.trim()}
            className="btn-primary disabled:opacity-60"
          >
            {saving ? 'Sending…' : 'Request parked domain'}
          </button>
        </form>
      ) : null}
    </div>
  )
}
